import React from 'react';

import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import adaptivePlugin from '@fullcalendar/adaptive'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction';
import luxonPlugin from '@fullcalendar/luxon3'
import { toLuxonDateTime } from '@fullcalendar/luxon3';

import { Constructors, Global } from '../models/Global'

import { CommonCalendarFunctions } from './CommonCalendarFunctions';
import { DateConversions } from '../utils/DateConversions'; 

import { ThemeColors } from '../Theme';

/**
 * Wrapper around the FullCalendar component, to display a calendar for all public Events in a community that a Patron will use to select an Event to sign up for.
 * Public events are shown as regular calendar events.
 * 
 * User must supply the following props:
 * 
 *     initialTimezone (String): The initial timezone to use for the calendar
 *     fetchSlots(start, end, successCallback, failureCallback): A function that fetches the slots from the server for the given time range.
 *                start: json string of the start date/time of the range
 *                end: json string of the end date/time of the range
 *                successCallback: A callback function that is called with the array of slots fetched from the server
 *                failureCallback: A callback function that is called with an error message if the fetch fails
 *  
 * Optional props:
 * 
 *     eventSelectCallback (function): A callback function that is called when the user selects an Event, called with the Event object as a parameter        
 *     loadingCallback (function): A callback function that is called when the calendar is loading events and rendering. The function is called with a boolean (true if loading)
 * 
 */


export class PatronEventsCalendar extends React.Component {

    _ref = React.createRef();
    _timezone;
    _eventSelectCallback;
    _didScrollToFirstEvent = false;
    _didScrollToNextYear = false;

    _selectedEventObj = null;
    
    // Cache the last fetch request, when resizing the calendar or when it re-renders itself it will refetch the events, but if the range is the same
    // as the last fetch, we don't need to refetch
    _eventCache = {start: null, end: null, events: []}
    
    _getApi = () => {
        if (this._ref.current)
            return this._ref.current.getApi();
        return null;
    }


    constructor(props) {
        super(props);
        this._timezone = props.initialTimezone;
        this._eventSelectCallback = props.eventSelectCallback;
    }

    componentDidMount() {
        // Before the calendar is mounted, nothing is fetched on initial render (because api is required), so we
        // refetch events after the component is mounted
        this._getApi().refetchEvents();
    }

    // See if the component should re-render, re-rendering is expensive because it refetches the events. So we only do it if we have to
    shouldComponentUpdate(nextProps, nextState) {
        return false;
    }

    // Clear all selected
    _clearSelected = () => {
        CommonCalendarFunctions.highlightEvent(null, true, this._selectedEventObj ? [this._selectedEventObj] : []);
        this._eventSelectCallback(null);
    }

    _gotoToday = () => {
        CommonCalendarFunctions.todayButtonPressed(this._getApi());
    }

    // Make the calendar go to the given luxon date
    _gotoDate = (date) => {
        const fullCalendarDate = date.toISO();
        const api = this._getApi();
        if (api)
            api.gotoDate(fullCalendarDate);
    }


    // -----------------------------  EVENT FETCHING  -----------------------------

    static _toFullCalendarEvent(slot) {
    
        return {
            id: slot.id,
            start: slot.start.toISO(),          // needs ISO string
            end: slot.end.toISO(),              // needs ISO string
            title: slot.title + (slot.isRecurring() ? " ↻" : ""),
            display: "block",
            color: ThemeColors.calendarDateButton,
            textColor: 'black',
            slotObject: slot
        }
    }


    // Since we are using the Luxon plugin, we must use the toLuxonDateTime function to convert the JSDate managed by the calendar
    // to a Luxon DateTime object
    _fullCalendarDateToLuxonDateTime = (date) => {
        return toLuxonDateTime(date, this._getApi());
    }



    // This function is called by the FullCalendar component to fetch events for the current view, the ref needs to exist so the component must
    // mount first. Fetch all the events within the specified time range, for the resources in the resources array, from the server
    _asyncFetchEvents = (fetchInfo, fetchEventsCallback, failureCallback) => {

        if (!this._getApi()) {       // component not mounted, no fetch
            fetchEventsCallback([]);
            return;
        }

        const start = CommonCalendarFunctions.fullCalendarDateToUTCJsonDateString(fetchInfo.start, this._getApi());
        const end = CommonCalendarFunctions.fullCalendarDateToUTCJsonDateString(fetchInfo.end, this._getApi());

        // If the range is the same as the last fetch, don't fetch again - just use the cache
        if (this._eventCache.start === start && this._eventCache.end === end) {
            console.log("Using cached events in range " + start + " to " + end);
            fetchEventsCallback(this._eventCache.events);
            return;
        }
        else
            this._eventCache = {start: null, end: null, events: []};    // clear cache

        console.log("Fetching events from " + start + " to " + end);
        this._clearSelected();

        // Tell the caller to fetch slots, the callback will be our fetchEventsCallback
        this.props.fetchSlots(start, end, 
                              (response) => this._fetchSlotsCallback(response, fetchEventsCallback, fetchInfo), 
                              (error) => failureCallback(error));
    }


    // Fetch Availability and Block slots from the server, and create clickable events for all the times that are available
    _fetchSlotsCallback = (response, fetchEventsCallback, fetchInfo) => {
        
        if (response) {

            const slots = response.map(json => Constructors.slotFromJson(json, this._timezone));

            if (slots.length === 0) {
                fetchEventsCallback([]);   // No events

                // If we haven't scrolled to the first event yet, since there are no events remaining this year, scroll to the start of next year
                if (!this._didScrollToFirstEvent && !this._didScrollToNextYear) {
                    const now = DateConversions.now(this._timezone);
                    const startOfNextYear = now.plus({ years: 1 }).startOf('year');
                    console.log("No events found, scrolling to start of next year: " + startOfNextYear.toISO());
                    this._gotoDate(startOfNextYear);
                    this._didScrollToNextYear = true;   // only jump once
                }
                return;
            }


            const earliestSlot = slots.reduce((earliest, slot) => {
                return (!earliest || slot.start < earliest.start) ? slot : earliest;
            }, null);

            const events = slots.map(slot => PatronEventsCalendar._toFullCalendarEvent(slot));

            const start = CommonCalendarFunctions.fullCalendarDateToUTCJsonDateString(fetchInfo.start, this._getApi());
            const end = CommonCalendarFunctions.fullCalendarDateToUTCJsonDateString(fetchInfo.end, this._getApi());
            this._eventCache = {start: start, end: end, events: events};   // put to cache

            fetchEventsCallback(events);

            // Find the earliest and latest times that are not available, so we can limit the calendar view to show only available times
            CommonCalendarFunctions.setButtonCalendarOptions(slots, this._getApi());

            
            if (!this._didScrollToFirstEvent) {
                this._didScrollToFirstEvent = true;     // only scroll once per page load

                if (!earliestSlot)
                    this._gotoToday();
                else {
                    console.log("Scrolling to earliest slot at " + earliestSlot.start.toISO());
                    this._gotoDate(earliestSlot.start);
                    setTimeout(() => {
                        const el = CommonCalendarFunctions.getSlotDomElement(earliestSlot.id);
                        if (el)
                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    });
                }
            }

        }
        else {
            console.error("Error fetching events");
            fetchEventsCallback([]);   // No events
        }
    }

    
    // --------------------------- CALENDAR CALLBACKS FOR USER CLICKS ----------------------------


    // When the user clicks on an event, we need to find the Slot object that corresponds to the event, and call the slotClickCallback. 
    _eventClick = (clickInfo) => {

        const eventObj = clickInfo.event;
        
        // Clear the old selected, and select this one
        if (!clickInfo.view.type.startsWith("list")) 
            CommonCalendarFunctions.highlightEvent(eventObj, true, this._selectedEventObj ? [this._selectedEventObj] : []);
        else {
            if (this._selectedEventObj) {
                const prevEl = CommonCalendarFunctions.getSlotDomElement(this._selectedEventObj.id);
                prevEl.style.backgroundColor = 'white';
                prevEl.style.color = 'black';
            }
            const el = CommonCalendarFunctions.getSlotDomElement(eventObj.id)
            el.style.backgroundColor = ThemeColors.calendarDateButtonSelected;
            el.style.color = 'white';
        }


        this._selectedEventObj = eventObj;

        if (this._eventSelectCallback)
            this._eventSelectCallback(eventObj.extendedProps.slotObject);
        
    }

    _eventMouseEnter = (info) =>{
        const slot = info.event._def.extendedProps.slotObject;
        CommonCalendarFunctions.showTippyPopover(slot, this._resources, info.el);
    }


    // -----------------------------  RENDERING  -----------------------------


    _eventMounted = (info) => { 

        // for non list views, style the event to look like a button, ignored in list views
        let style = info.el.getAttribute("style");
        style += "border-radius: 3px; opacity: 1.0; border: 1px solid gray; margin: 1px; ";  // make the event look like a button
        info.el.setAttribute("style", style);

        // Remove the class that triggers hover styling on the list
        info.el.classList.remove("fc-list-event");

        const slot = info.event._def.extendedProps.slotObject;
        info.el.setAttribute("slotid", slot.id);              // mark this so we know how to find it
    
        if (info.view.type === 'list90days') {
            let toInject = [slot.locationString, slot.spacesLeft()];
            for (let i = 0; i < toInject.length; i++) {
                let columnElement = document.createElement('td');
                columnElement.className = "fc-list-ancillary-item";         // mark this so we know how to find it
                columnElement.textContent = toInject[i];
                info.el.append(columnElement);
            }
        }
    }

    // Custom column headers for List view
    _dayHeaderMounted = (info) => {
        if (info.view.type === 'list90days') {
            const listFontSize = window.innerWidth < 700 ? '8pt' : '11pt';

            for (let child of info.el.children) {
                for (let subchild of child.children)
                    subchild.style="text-align: left; background-color: " + (info.isToday ? ThemeColors.todayTan : ThemeColors.listHeaderColor) + "; font-size: " + listFontSize;
            }

            let toInject = ['Location', 'Spaces Left'];
            for (let i = 0; i < toInject.length; i++) {
                let columnElement = document.createElement('th');
                let div = document.createElement('div');
                div.className="fc-list-day-cushion fc-cell-shaded";
                if (info.isToday)
                    div.style="text-align: left; background-color: " + ThemeColors.todayTan + "; font-size: " + listFontSize;
                else
                    div.style="text-align: left; background-color: " + ThemeColors.listHeaderColor + "; font-size: " + listFontSize;
                div.textContent = toInject[i];
                columnElement.append(div);
                info.el.append(columnElement);
            }

        }
    }

    render() {

       
        const customButtons = { customToday: {text: 'Today', click: this._gotoToday, hint: 'Go to Today'}, };      
        const height = window.innerWidth < 600 ? window.innerHeight * 0.6 : window.innerHeight * 0.7;

        return (
            <FullCalendar schedulerLicenseKey={Global.fullCalendarLicenseKey} 
                            ref={this._ref}
                            plugins={[interactionPlugin, dayGridPlugin, timeGridPlugin, 
                                      listPlugin, adaptivePlugin, luxonPlugin]}
                            timeZone={this._timezone}
                            initialView='list90days'
                            height={height}
                            expandRows={true}
                            nowIndicator={true}
                            monthStartFormat={{month: 'short', day: 'numeric'}}
                            slotDuration={'00:30:00'}
                            eventTimeFormat={{
                                hour: 'numeric',
                                minute: '2-digit',
                                meridiem: 'narrow'
                            }}
                            slotLabelFormat={{ hour: 'numeric', minute: 'numeric', merdiem: 'short' }}
                            navLinks={true}
                            allDaySlot={false}
                            headerToolbar={{
                                left: 'prev,next customToday',
                                center: 'title',
                                right: 'dayGridYear,timeGridWeek,timeGridDay,list90days',
                            }}      
                            buttonText={{
                                dayGridYear: 'Month',
                            }}   
                            views={{
                                list90days: {
                                    type: 'list',
                                    duration: { days: 90 }
                                }
                            }}             
                            events={{events: this._asyncFetchEvents, id: 'server'}}  
                            eventClick={this._eventClick}
                            eventMouseEnter={this._eventMouseEnter}
                            eventDidMount={this._eventMounted}     
                            datesSet={CommonCalendarFunctions.setNextPreviousButtonText}
                            dayHeaderDidMount={this._dayHeaderMounted}
                            loading={this.props.loadingCallback}
                            customButtons={customButtons}
            />

        );
    }


}
